import type { Brand, Locale } from "@borealis/layout-express";
import type { ItemResponse, OrganisationResponse } from "@finn-no/mobility-dealer-types";
import type { EidResponse, MobilityAd } from "@mobility-item-page/types";
import { createContext, type FC, type ReactNode, useContext } from "react";
import type { ContactFormVariant } from "../../types/contactFormVariants.ts";
import { type DealerItemResult, DealerItemStatus, type ErrorStatus } from "../../types.ts";

export type ExternalProps = {
  isMobile: boolean;
  brand: Brand;
  locale: Locale;
  adViewType?: MobilityAd["adViewType"];
  isLoggedIn: boolean;
  isPreview: boolean;
  eidAuth: EidResponse;
  openContactForm: boolean;

  // Features
  homepageSurvey: boolean;
  reservationEnabled: boolean;
  isItemReserved: boolean;
  newContactForm: boolean;
  newContactFormV2: boolean;
  newContactFormV3: ContactFormVariant | undefined;
};
type ResolvedProps = {
  phoneNumberCallbackUrl?: string;
};
/**
 * This React Context provides the data fetched from the "mobility company profile" api
 */
export type Context = {
  dealerItemResult: DealerItemResult | null;
  external: ExternalProps;
};

const DealerContext = createContext<Context>({
  dealerItemResult: null,
  external: {
    isMobile: false,
    brand: "FINN",
    locale: "no",
    isLoggedIn: false,
    isPreview: false,
    adViewType: undefined,
    homepageSurvey: false,
    reservationEnabled: false,
    isItemReserved: false,
    eidAuth: { status: "NOT_APPLICABLE", userMustVerify: false },
    newContactForm: false,
    newContactFormV2: false,
    newContactFormV3: undefined,
    openContactForm: false,
  },
});

type Props = {
  dealerItemResult: DealerItemResult | null;
  external: ExternalProps;
  children: ReactNode;
};
export function DealerDataProvider(props: Props) {
  const { children, dealerItemResult, external } = props;
  return <DealerContext value={{ dealerItemResult, external }}>{children}</DealerContext>;
}

type ValidDealerContext = {
  status: DealerItemStatus.Ok;
  itemData: Omit<ItemResponse, "organisationData">;
  organisationData: OrganisationResponse;
  external: ExternalProps;
  resolved: ResolvedProps;
};

type InvalidDealerContext = {
  status: ErrorStatus;
  external: ExternalProps;
};

const isWrappedInDealerProviderCheck = (dealerItemResult: DealerItemResult | null) => {
  return dealerItemResult !== null;
};

export function useDealerContext(): ValidDealerContext | InvalidDealerContext {
  const { dealerItemResult, external } = useContext(DealerContext);
  if (
    !isWrappedInDealerProviderCheck ||
    !dealerItemResult?.status ||
    dealerItemResult.status === DealerItemStatus.NotDealerAd
  ) {
    throw new Error(
      `dealerData (${dealerItemResult}) is not defined. Ensure you are within a DealerDataProvider and that your component is wrapped in "withDataGuard(...Component)"`,
    );
  }

  const { status } = dealerItemResult;
  if (status !== DealerItemStatus.Ok) {
    return { status, external };
  }

  const { data } = dealerItemResult;
  const { organisationData, ...itemData } = data ?? {};

  const resolved: ResolvedProps = {
    phoneNumberCallbackUrl: itemData.integrityToken
      ? `/mobility/api/phone/${itemData.adId}/${itemData.integrityToken}`
      : undefined,
  };

  return { status, organisationData, itemData, external, resolved };
}

// Expecting that dealerItemResult status is "ok"
export function useValidDealerContext(): ValidDealerContext {
  const { dealerItemResult, external } = useContext(DealerContext);
  if (!isWrappedInDealerProviderCheck) {
    throw new Error(
      `dealerData (${dealerItemResult}) is not defined. Ensure you are within a DealerDataProvider and that your component is wrapped in "withDataGuard(...Component)"`,
    );
  }
  if (!dealerItemResult?.status || dealerItemResult.status !== DealerItemStatus.Ok) {
    throw new Error(
      `dealerData (${dealerItemResult}) Status is not "ok". Received: ${dealerItemResult?.status}. Use useDealerContext when you want to handle these cases.`,
    );
  }
  const { status, data } = dealerItemResult;
  const { organisationData, ...itemData } = data ?? {};

  const resolved: ResolvedProps = {
    phoneNumberCallbackUrl: itemData.integrityToken
      ? `/mobility/api/phone/${itemData.adId}/${itemData.integrityToken}`
      : undefined,
  };

  return { status, organisationData, itemData, external, resolved };
}

// Returns item data without organisationData or null if not available. This is safe to use outside of data guards.
export function useSafeDealerItemContext(): Omit<ItemResponse, "organisationData"> | null {
  const { dealerItemResult } = useContext(DealerContext);

  if (!isWrappedInDealerProviderCheck) {
    return null;
  }

  if (dealerItemResult?.status === "ok") {
    const { organisationData, ...itemData } = dealerItemResult.data ?? {};
    return itemData;
  }

  return null;
}

export const withDataGuard = <Props,>(Component: FC<Props>): FC<Props> => {
  return (props) => {
    const { dealerItemResult } = useContext(DealerContext);

    if (dealerItemResult === null || dealerItemResult?.status !== DealerItemStatus.Ok) {
      return null;
    }

    return Component(props);
  };
};

export const withDataGuardFallback = <Props,>(Component: FC<Props>, fallback?: () => ReactNode): FC<Props> => {
  return (props) => {
    const { status } = useDealerContext();

    if (status === DealerItemStatus.NotDealerAd) {
      return null;
    }
    if (status !== DealerItemStatus.Ok) {
      if (fallback) {
        return fallback();
      }
      return null;
    }

    return Component(props);
  };
};
